/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import type { Badge, Host, Script } from '../recaptcha.types';

import { generateRecaptchaScriptSrc } from './generateRecaptchaScriptSrc';

interface InjectRecaptchaScriptParams extends Script {
    badge?: Exclude<Badge, 'hidden'>;
    hl?: string;
    host?: Host;
    isEnterprise?: boolean;
    render: string;
    onload: () => void;
}

/**
 * Injects the Google ReCaptcha script into the DOM.
 *
 * @param {InjectRecaptchaScriptParams} params - The parameters for injecting the script.
 * @param {Function} params.onload - The callback function to execute when the script has finished loading.
 * @param {string} [params.appendTo] - The element to which the script should be appended.
 * @param {boolean} [params.isEnterprise] - Indicates whether the ReCaptcha is an enterprise version.
 * @param {string} [params.host] - The host URL for the ReCaptcha.
 * @param {string} [params.render] - The rendering method for the ReCaptcha.
 * @param {string} [params.badge] - The badge position for the ReCaptcha.
 * @param {string} [params.hl] - The language code for the ReCaptcha.
 * @return {void} This function does not return a value.
 */
export function injectRecaptchaScript({
    onload,
    appendTo = 'head',
    isEnterprise = true,
    host,
    render,
    badge,
    hl,
    ...params
}: InjectRecaptchaScriptParams) {
    const recaptchaScriptSrc = generateRecaptchaScriptSrc({
        host,
        isEnterprise,
        render,
        hl,
        badge,
    });

    const script = document.createElement('script');

    Object.entries(params).forEach(([key, value]) => {
        script.setAttribute(key, value?.toString() ?? '');
    });

    script.src = recaptchaScriptSrc;
    script.onload = onload;

    document[appendTo].appendChild(script);
}
